import { defineStore } from 'pinia'
import { computed, onScopeDispose, type Ref, ref, watch } from 'vue'

import { i18n } from '@/i18n'
import { parseCallableError } from '@/lib/firebase/errors'
import { resolveStorageDownloadUrl } from '@/lib/firebase/storage'
import type { Subscription } from '@/lib/firebase/subscribe'
import { createRetainer, onRetainedTeardown } from '@/lib/live/retainer'
import {
  type DesignDocument,
  parseDesignDocument,
  resolveDesignHtml,
} from '@/lib/design/design-document'
import { probeDesignPageCount } from '@/lib/design/design-page-count'
import {
  type FontFaces,
  type TemplateDoc,
  loadFontFaces,
  parseTemplate,
} from '@/lib/templates/template-loader'
import {
  cancelDesignEdit as repoCancelDesignEdit,
  designPostMedia as repoDesignPostMedia,
  editDesign as repoEditDesign,
  designRenderStoragePath,
  saveDesign as repoSaveDesign,
  subscribeDesign as repoSubscribeDesign,
  subscribeDesignEditRuns as repoSubscribeDesignEditRuns,
  subscribeDesignRuns as repoSubscribeDesignRuns,
  subscribeLatestPostDesignRun,
  subscribeRecentDesigns as repoSubscribeRecentDesigns,
  undoDesignEdit as repoUndoDesignEdit,
} from '@/repositories/design'
import { designFiles } from '@/repositories/design-files'
import { storageTemplateFiles } from '@/repositories/templates'
import { useAuthStore } from '@/stores/auth'
import { useKeyedSubscription } from '@/stores/keyed-subscription'
import { useToastStore } from '@/stores/toast'
import {
  type Design,
  type DesignEditRun,
  type DesignRun,
  isDesignEditRunLive,
  isDesignRunLive,
  isDesignRunMade,
} from '@/types/design'
import { failed, isLoaded, loaded, loading, type LoadState } from '@/types/load-state'

interface DesignScope {
  workspaceId: string
  brandId: string
}

/** A design's parsed `design.html` at one revision, with the template bundle's font faces and the design's own copy of its template (`null` when that file cannot be read). */
export interface LoadedDesignDocument {
  revision: string | null
  document: DesignDocument
  fontFaces: FontFaces
  template: TemplateDoc | null
}

/** What came of saving the edited design file. */
export type SaveDesignOutcome =
  | { status: 'saved'; revision: string }
  | { status: 'stale' }
  | { status: 'failed'; message: string }

const DESIGN_FILE = 'design.html'
const TEMPLATE_FILE = 'template.html'
const TEMPLATE_ID = 'template'

const SAVE_REFUSAL_KEYS: Record<string, string> = {
  design_html_invalid: 'design.editor.save.refused.invalid',
  design_styles_changed: 'design.editor.save.refused.styles',
  editor_role_required: 'design.editor.save.refused.editorRole',
}

const DESIGN_MEDIA_REFUSAL_KEYS: Record<string, string> = {
  post_media_missing: 'content.post.designMediaRefused.noMedia',
  post_not_designable: 'content.post.designMediaRefused.notDesignable',
  design_run_already_active: 'content.post.designMediaRefused.running',
  editor_role_required: 'content.post.designMediaRefused.editorRole',
}

/** What Raldo needs to edit one page of a design. */
export interface StartDesignEditInput {
  designId: string
  page: number
  eid: string | null
  instruction: string
}

const EDIT_REFUSAL_KEYS: Record<string, string> = {
  design_edit_already_active: 'design.editor.raldo.refused.alreadyActive',
  design_not_ready: 'design.editor.raldo.refused.notReady',
  design_edit_instruction_empty: 'design.editor.raldo.refused.instructionEmpty',
  design_edit_instruction_too_long: 'design.editor.raldo.refused.instructionTooLong',
  design_edit_page_missing: 'design.editor.raldo.refused.pageMissing',
  design_edit_eid_invalid: 'design.editor.raldo.refused.elementMissing',
  editor_role_required: 'design.editor.raldo.refused.editorRole',
}

const UNDO_REFUSAL_KEYS: Record<string, string> = {
  design_edit_superseded: 'design.editor.raldo.refused.superseded',
  design_edit_not_undoable: 'design.editor.raldo.refused.notUndoable',
  design_edit_snapshot_incomplete: 'design.editor.raldo.refused.snapshotIncomplete',
  workspace_snapshot_generation_unavailable: 'design.editor.raldo.refused.snapshotUnavailable',
  editor_role_required: 'design.editor.raldo.refused.editorRole',
}

export const useDesignStore = defineStore('design', () => {
  const designsById = ref<Record<string, LoadState<Design>>>({})
  const runsByDesignId = ref<Record<string, LoadState<DesignRun[]>>>({})
  const editRunsByDesignId = ref<Record<string, LoadState<DesignEditRun[]>>>({})
  const recentDesigns = ref<LoadState<Design[]>>(loading()) as Ref<LoadState<Design[]>>
  const pageCountByRender = ref<Record<string, number>>({})
  const documentsById = ref<Record<string, LoadState<LoadedDesignDocument>>>({})
  const postRun = useKeyedSubscription<DesignRun | null>(subscribeLatestPostDesignRun)

  const documentReads = new Map<string, { revision: string | null; done: Promise<void> }>()
  const revisionWatchers = new Map<string, () => void>()
  let bundleFiles: ReturnType<typeof storageTemplateFiles> | null = null
  let fontFacesRead: Promise<FontFaces> | null = null

  const designSubs = new Map<string, { sub: Subscription<Design>; stop: () => void }>()
  const runSubs = new Map<string, { sub: Subscription<DesignRun[]>; stop: () => void }>()
  const editRunSubs = new Map<string, { sub: Subscription<DesignEditRun[]>; stop: () => void }>()
  let recentSub: Subscription<Design[]> | null = null
  let stopRecentWatch: (() => void) | null = null

  function currentScope(): DesignScope | null {
    const auth = useAuthStore()
    const workspaceId = auth.claims?.workspaceId
    const brandId = auth.claims?.brandId
    if (!workspaceId || !brandId) return null
    return { workspaceId, brandId }
  }

  /** Subscribes to one design's projection and run history; a repeat call is a no-op. */
  function subscribeToDesign(designId: string): void {
    if (!designSubs.has(designId)) {
      const scope = currentScope()
      if (!scope) {
        designsById.value = {
          ...designsById.value,
          [designId]: failed(i18n.global.t('common.noWorkspaceSelected')),
        }
      } else {
        designsById.value = { ...designsById.value, [designId]: loading() }
        const sub = repoSubscribeDesign({ ...scope, designId })
        const stop = watch(
          sub.state,
          (state) => {
            designsById.value = { ...designsById.value, [designId]: state }
          },
          { immediate: true },
        )
        designSubs.set(designId, { sub, stop })
      }
    }

    if (!runSubs.has(designId)) {
      const scope = currentScope()
      if (!scope) {
        runsByDesignId.value = {
          ...runsByDesignId.value,
          [designId]: failed(i18n.global.t('common.noWorkspaceSelected')),
        }
        return
      }
      runsByDesignId.value = { ...runsByDesignId.value, [designId]: loading() }
      const sub = repoSubscribeDesignRuns({ ...scope, designId })
      const stop = watch(
        sub.state,
        (state) => {
          runsByDesignId.value = { ...runsByDesignId.value, [designId]: state }
        },
        { immediate: true },
      )
      runSubs.set(designId, { sub, stop })
    }

    if (!editRunSubs.has(designId)) {
      const scope = currentScope()
      if (!scope) return
      editRunsByDesignId.value = { ...editRunsByDesignId.value, [designId]: loading() }
      const sub = repoSubscribeDesignEditRuns({ ...scope, designId })
      const stop = watch(
        sub.state,
        (state) => {
          editRunsByDesignId.value = { ...editRunsByDesignId.value, [designId]: state }
        },
        { immediate: true },
      )
      editRunSubs.set(designId, { sub, stop })
    }
  }

  function renderKey(designId: string, renderVersion: number): string {
    return `${designId}/v${renderVersion}`
  }

  function dropPageCounts(designId: string): void {
    const prefix = `${designId}/`
    pageCountByRender.value = Object.fromEntries(
      Object.entries(pageCountByRender.value).filter(([key]) => !key.startsWith(prefix)),
    )
  }

  function unsubscribeFromDesign(designId: string): void {
    designSubs.get(designId)?.sub.stop()
    designSubs.get(designId)?.stop()
    designSubs.delete(designId)
    runSubs.get(designId)?.sub.stop()
    runSubs.get(designId)?.stop()
    runSubs.delete(designId)
    editRunSubs.get(designId)?.sub.stop()
    editRunSubs.get(designId)?.stop()
    editRunSubs.delete(designId)

    const nextDesigns = { ...designsById.value }
    delete nextDesigns[designId]
    designsById.value = nextDesigns

    const nextRuns = { ...runsByDesignId.value }
    delete nextRuns[designId]
    runsByDesignId.value = nextRuns

    const nextEditRuns = { ...editRunsByDesignId.value }
    delete nextEditRuns[designId]
    editRunsByDesignId.value = nextEditRuns

    dropPageCounts(designId)

    revisionWatchers.get(designId)?.()
    revisionWatchers.delete(designId)
    documentReads.delete(designId)
    const nextDocuments = { ...documentsById.value }
    delete nextDocuments[designId]
    documentsById.value = nextDocuments
  }

  function openRecentDesigns(): void {
    if (recentSub) return
    const scope = currentScope()
    if (!scope) {
      recentDesigns.value = failed(i18n.global.t('common.noWorkspaceSelected'))
      return
    }
    recentDesigns.value = loading()
    const sub = repoSubscribeRecentDesigns(scope)
    recentSub = sub
    stopRecentWatch = watch(
      sub.state,
      (state) => {
        recentDesigns.value = state
      },
      { immediate: true },
    )
  }

  function closeRecentDesigns(): void {
    recentSub?.stop()
    recentSub = null
    stopRecentWatch?.()
    stopRecentWatch = null
    recentDesigns.value = loading()
  }

  const recentRetainer = createRetainer({ open: openRecentDesigns, close: closeRecentDesigns })
  onScopeDispose(recentRetainer.dispose)
  onScopeDispose(
    onRetainedTeardown(() => {
      for (const designId of new Set([
        ...designSubs.keys(),
        ...runSubs.keys(),
        ...editRunSubs.keys(),
      ])) {
        unsubscribeFromDesign(designId)
      }
    }),
  )

  function designById(designId: string): Design | null {
    const state = designsById.value[designId]
    return state && isLoaded(state) ? state.value : null
  }

  function liveRunFor(designId: string): DesignRun | null {
    const state = runsByDesignId.value[designId]
    if (!state || !isLoaded(state)) return null
    return state.value.find((run) => isDesignRunLive(run)) ?? null
  }

  function latestRunFor(designId: string): DesignRun | null {
    const state = runsByDesignId.value[designId]
    if (!state || !isLoaded(state)) return null
    return state.value[0] ?? null
  }

  /** Raldo's edits of the design, newest first. */
  function editRunsFor(designId: string): DesignEditRun[] {
    const state = editRunsByDesignId.value[designId]
    return state && isLoaded(state) ? state.value : []
  }

  /** The design's newest edit run, live or finished; `null` when Raldo never edited it. */
  function latestEditRunFor(designId: string): DesignEditRun | null {
    return editRunsFor(designId)[0] ?? null
  }

  /** The edit Raldo is making right now, or `null`. */
  function liveEditRunFor(designId: string): DesignEditRun | null {
    return editRunsFor(designId).find((run) => isDesignEditRunLive(run)) ?? null
  }

  /** The brand's recent designs whose latest run is completed; a design with no subscribed run history is excluded. */
  const doneRecentDesigns = computed<Design[]>(() => {
    const state = recentDesigns.value
    if (!isLoaded(state)) return []
    return state.value.filter((item) => isDesignRunMade(latestRunFor(item.id)))
  })

  function isRunsLoading(designId: string): boolean {
    const state = runsByDesignId.value[designId]
    return !state || state.status === 'loading'
  }

  /** How many pages one render version has, probed and cached on first ask; `undefined` until the probe settles. */
  function pageCountFor(designId: string, renderVersion: number): number | undefined {
    return pageCountByRender.value[renderKey(designId, renderVersion)]
  }

  async function pageExists(
    designId: string,
    renderVersion: number,
    page: number,
    scope: DesignScope,
  ): Promise<boolean> {
    try {
      await resolveStorageDownloadUrl(
        designRenderStoragePath(scope.workspaceId, scope.brandId, designId, renderVersion, page),
      )
      return true
    } catch {
      return false
    }
  }

  /** Counts the pages of one render version; a design with no render has none. */
  async function probePageCount(designId: string, renderVersion: number | null): Promise<number> {
    if (renderVersion === null) return 0
    const key = renderKey(designId, renderVersion)
    const cached = pageCountByRender.value[key]
    if (cached !== undefined) return cached

    const scope = currentScope()
    if (!scope) return 0

    const count = await probeDesignPageCount((page) =>
      pageExists(designId, renderVersion, page, scope),
    )
    pageCountByRender.value = { ...pageCountByRender.value, [key]: count }
    return count
  }

  async function renderUrlsFor(
    designId: string,
    renderVersion: number | null,
    pageCount: number,
  ): Promise<(string | null)[]> {
    const scope = currentScope()
    if (!scope || renderVersion === null) return Array.from({ length: pageCount }, () => null)

    return Promise.all(
      Array.from({ length: pageCount }, (_, index) =>
        resolveStorageDownloadUrl(
          designRenderStoragePath(
            scope.workspaceId,
            scope.brandId,
            designId,
            renderVersion,
            index + 1,
          ),
        ).catch(() => null),
      ),
    )
  }

  /** The design's projection once its subscription has settled. */
  function settledDesign(designId: string): Promise<LoadState<Design>> {
    return new Promise((resolve) => {
      let done = false
      let stop: (() => void) | null = null
      stop = watch(
        () => designsById.value[designId],
        (state) => {
          if (done || !state || state.status === 'loading') return
          done = true
          stop?.()
          resolve(state)
        },
        { immediate: true },
      )
      if (done) stop()
    })
  }

  function bundleFontFaces(): Promise<FontFaces> {
    bundleFiles ??= storageTemplateFiles()
    if (!fontFacesRead) {
      const read = loadFontFaces(bundleFiles)
      read.catch(() => {
        if (fontFacesRead === read) fontFacesRead = null
      })
      fontFacesRead = read
    }
    return fontFacesRead
  }

  function setDocument(designId: string, state: LoadState<LoadedDesignDocument>): void {
    documentsById.value = { ...documentsById.value, [designId]: state }
  }

  async function readTemplate(
    html: string,
    files: ReturnType<typeof designFiles>,
    bundle: NonNullable<typeof bundleFiles>,
    fontFaces: FontFaces,
    originals: Map<string, string>,
  ): Promise<TemplateDoc | null> {
    try {
      const resolved = await resolveDesignHtml(html, files, bundle, originals)
      return parseTemplate(TEMPLATE_ID, resolved.html, fontFaces)
    } catch {
      return null
    }
  }

  async function readDocument(design: Design, scope: DesignScope): Promise<LoadedDesignDocument> {
    bundleFiles ??= storageTemplateFiles()
    const bundle = bundleFiles
    const files = designFiles(scope, design.folder)
    const [fontFaces, html, templateHtml] = await Promise.all([
      bundleFontFaces(),
      files.readText(DESIGN_FILE),
      files.readText(TEMPLATE_FILE).catch(() => null),
    ])
    const originals = new Map<string, string>()
    const resolved = await resolveDesignHtml(html, files, bundle, originals)
    const template =
      templateHtml === null
        ? null
        : await readTemplate(templateHtml, files, bundle, fontFaces, originals)
    return {
      revision: design.revision,
      document: parseDesignDocument(resolved.html, fontFaces.families, originals),
      fontFaces,
      template,
    }
  }

  async function refreshDocument(designId: string): Promise<void> {
    const settled = await settledDesign(designId)
    if (!isLoaded(settled)) {
      setDocument(designId, failed(i18n.global.t('design.view.documentError')))
      return
    }
    const design = settled.value
    const current = documentReads.get(designId)
    if (current && current.revision === design.revision) return current.done
    if (design.revision === null) {
      documentReads.set(designId, { revision: null, done: Promise.resolve() })
      return
    }
    const scope = currentScope()
    if (!scope) {
      setDocument(designId, failed(i18n.global.t('common.noWorkspaceSelected')))
      return
    }
    const isCurrent = () => documentReads.get(designId)?.revision === design.revision
    const done = readDocument(design, scope).then(
      (document) => {
        if (isCurrent()) setDocument(designId, loaded(document))
      },
      () => {
        if (isCurrent()) setDocument(designId, failed(i18n.global.t('design.view.documentError')))
      },
    )
    documentReads.set(designId, { revision: design.revision, done })
    return done
  }

  /** The design's parsed `design.html`; loading until `loadDocument` has read it. */
  function documentFor(designId: string): LoadState<LoadedDesignDocument> {
    return documentsById.value[designId] ?? loading()
  }

  /** The design's own copy of its template, once its document has loaded; `null` before that or when the file cannot be read. */
  function templateFor(designId: string): TemplateDoc | null {
    const state = documentFor(designId)
    return isLoaded(state) ? state.value.template : null
  }

  /** Reads and parses the design's `design.html`, and again whenever the design's revision changes. */
  async function loadDocument(designId: string): Promise<void> {
    subscribeToDesign(designId)
    if (!revisionWatchers.has(designId)) {
      revisionWatchers.set(
        designId,
        watch(
          () => designById(designId)?.revision,
          (revision, previous) => {
            if (revision !== previous) void refreshDocument(designId)
          },
        ),
      )
    }
    await refreshDocument(designId)
  }

  /** Reads the design file again even though its revision is the one already read. */
  async function reloadDocument(designId: string): Promise<void> {
    documentReads.delete(designId)
    await loadDocument(designId)
  }

  /** Records a revision this app wrote, so the revision watcher does not read the file it just saved. */
  function markSaved(designId: string, revision: string): void {
    if (!designSubs.has(designId)) return
    documentReads.set(designId, { revision, done: Promise.resolve() })
    const state = documentFor(designId)
    if (isLoaded(state)) setDocument(designId, loaded({ ...state.value, revision }))
  }

  /** Writes the edited design file; the outcome says whether it landed, the file changed elsewhere, or it was refused. */
  async function saveDocument(
    designId: string,
    html: string,
    expectedRevision: string,
  ): Promise<SaveDesignOutcome> {
    try {
      const { revision } = await repoSaveDesign({ designId, html, expectedRevision })
      markSaved(designId, revision)
      return { status: 'saved', revision }
    } catch (cause) {
      const parsed = parseCallableError(cause)
      if (parsed.reason === 'design_revision_stale') return { status: 'stale' }
      const refusalKey = parsed.reason ? SAVE_REFUSAL_KEYS[parsed.reason] : undefined
      return {
        status: 'failed',
        message: refusalKey ? i18n.global.t(refusalKey) : parsed.message,
      }
    }
  }

  /** The newest design run of a post a holder retains through `retainPostRunFor`, or `null`. */
  function latestRunForPost(postId: string): DesignRun | null {
    const state = postRun.state.value
    if (postRun.key.value !== postId || !isLoaded(state)) return null
    return state.value
  }

  /** Starts the design of a post's media; `false` on failure (toasted). */
  async function designPostMedia(postId: string): Promise<boolean> {
    try {
      await repoDesignPostMedia({ postId })
      return true
    } catch (cause) {
      const parsed = parseCallableError(cause)
      const refusalKey = parsed.reason ? DESIGN_MEDIA_REFUSAL_KEYS[parsed.reason] : undefined
      useToastStore().error(
        i18n.global.t('content.post.designMediaFailed'),
        refusalKey ? i18n.global.t(refusalKey) : parsed.message,
      )
      return false
    }
  }

  function toastRefusal(title: string, cause: unknown, keys: Record<string, string>): void {
    const parsed = parseCallableError(cause)
    const key = parsed.reason ? keys[parsed.reason] : undefined
    useToastStore().error(title, key ? i18n.global.t(key) : parsed.message)
  }

  /** Asks Raldo to edit one page of a design; the run id, or `null` on failure (toasted). */
  async function startDesignEdit(input: StartDesignEditInput): Promise<string | null> {
    try {
      const { runId } = await repoEditDesign(input)
      return runId
    } catch (cause) {
      toastRefusal(i18n.global.t('design.editor.raldo.startFailed'), cause, EDIT_REFUSAL_KEYS)
      return null
    }
  }

  /** Stops a running edit; `false` on failure (toasted). */
  async function cancelDesignEdit(runId: string): Promise<boolean> {
    try {
      await repoCancelDesignEdit({ runId })
      return true
    } catch (cause) {
      toastRefusal(i18n.global.t('design.editor.raldo.stopFailed'), cause, EDIT_REFUSAL_KEYS)
      return false
    }
  }

  /** Restores the design to before a finished edit; `false` on failure (toasted). */
  async function undoDesignEdit(runId: string): Promise<boolean> {
    try {
      await repoUndoDesignEdit({ runId })
      return true
    } catch (cause) {
      toastRefusal(i18n.global.t('design.editor.raldo.undoFailed'), cause, UNDO_REFUSAL_KEYS)
      return false
    }
  }

  return {
    designsById,
    editRunsByDesignId,
    runsByDesignId,
    recentDesigns,
    subscribeToDesign,
    unsubscribeFromDesign,
    retainRecentDesigns: recentRetainer.retain,
    designById,
    liveRunFor,
    latestRunFor,
    editRunsFor,
    latestEditRunFor,
    liveEditRunFor,
    startDesignEdit,
    cancelDesignEdit,
    undoDesignEdit,
    doneRecentDesigns,
    isRunsLoading,
    pageCountFor,
    probePageCount,
    renderUrlsFor,
    retainPostRunFor: postRun.retain,
    latestRunForPost,
    designPostMedia,
    documentFor,
    templateFor,
    loadDocument,
    reloadDocument,
    markSaved,
    saveDocument,
  }
})
